import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Music, Loader2, ChevronRight, MailCheck, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

/**
 * iter122 — Profile section listing every festival the user has pinned.
 *
 * Shows the next-up first (date order), with their D-N countdown and the
 * status of their 14-day reminder email (pending / sent). Mounted only
 * when the section has something to render so it doesn't add an empty
 * panel to the profile.
 */
export default function SavedFestivalsSection() {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/me/festivals").then(({ data }) => {
      if (alive) setFestivals(data?.festivals || []);
    }).catch(() => { /* anon user just hides */ })
    .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Empty section is hidden — no clutter on profiles with no saved festivals.
  if (!loading && festivals.length === 0) return null;

  return (
    <section
      className="border-2 hairline-strong bg-white p-6 mb-8"
      data-testid="profile-saved-festivals"
    >
      <header className="mb-4">
        <div className="eyebrow text-[#ff4500] inline-flex items-center gap-1.5">
          <Music className="w-3 h-3" strokeWidth={2.4} /> Festivals you've saved
        </div>
        <h2 className="font-display text-xl mt-1">Your festival reminders</h2>
        <p className="text-sm text-[#555] leading-relaxed mt-1.5">
          We'll email you 14 days before each one — packing list, weather hint, closest pitches.
        </p>
      </header>

      {loading ? (
        <div className="flex items-center text-sm text-[#555]">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your festivals…
        </div>
      ) : (
        <div className="space-y-2.5">
          {festivals.map((f) => (
            <Link
              key={f.slug}
              to={`/festivals/${f.slug}`}
              className="group flex items-center justify-between gap-3 p-3 border hairline bg-[#fafafa] hover:bg-white hover:shadow-[0_1px_0_0_#0e0e0e] transition-all"
              data-testid={`profile-saved-festival-${f.slug}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-1.5 h-10" style={{ background: f.hero_color || "#ff4500" }} />
                <div className="min-w-0">
                  <div className="font-display text-base leading-tight truncate group-hover:text-[#ff4500] transition-colors">
                    {f.name}
                  </div>
                  <div className="text-[11px] mono text-[#666] uppercase tracking-wider flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(f.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </span>
                    <span className="inline-flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{f.location_label}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {f.days_until != null && f.days_until >= 0 && (
                  <span
                    className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-[10px] mono uppercase tracking-wider ${
                      f.days_until <= 14 ? "bg-[#ff4500] text-white" : "bg-[#0e0e0e] text-white"
                    }`}
                  >
                    <Sparkles className="w-3 h-3" />
                    {f.days_until === 0 ? "Today" : `D-${f.days_until}`}
                  </span>
                )}
                {f.reminder_sent_at ? (
                  <span
                    className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-[10px] mono uppercase tracking-wider bg-[#16a34a] text-white"
                    title="Reminder email sent"
                  >
                    <MailCheck className="w-3 h-3" /> Sent
                  </span>
                ) : null}
                <ChevronRight className="w-4 h-4 text-[#666] group-hover:text-[#ff4500] transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
